import './checkbox.css';
import React, { useState } from 'react';
import { useApiData } from '../../../contexts/apiService';

interface CheckboxProps {
    label?: string;
    onChange?: (checked: boolean) => void;
    checked?: boolean;
    defaultChecked?: boolean;
    size?: number;
    style?: 'default' | 'minus';
    isDisabled?: boolean;
    editConfig?: CheckboxEditConfig;
    ghostUnchecked?: boolean;
    labelGap?: number;
}

interface CheckboxEditConfig {
    tableName: string;
    rowId: string | number;
    columnName: string;
    parentId?: number;
    onSuccess?: () => void;
    onError?: () => void;
}

const Checkbox: React.FC<CheckboxProps> = ({ 
    label, 
    onChange,
    checked,
    defaultChecked = false,
    size = 18,
    style = 'default',
    isDisabled = false,
    editConfig,
    ghostUnchecked = false,
    labelGap = 0,
}) => {

    const { handleEditOrder } = useApiData();
    const [internalChecked, setInternalChecked] = useState(defaultChecked);
    const [isLoading, setIsLoading] = useState(false);

    // Use controlled state if provided, otherwise use internal state
    const isChecked = checked !== undefined ? checked : internalChecked;

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isDisabled || isLoading) return;

        const newChecked = e.target.checked;
        
        // Only update internal state if not controlled
        if (checked === undefined) {
            setInternalChecked(newChecked);
        }
        
        onChange?.(newChecked);

        if (editConfig) {
            const oldValue = isChecked ? 1 : 0;
            const newValue = newChecked ? 1 : 0;

            setIsLoading(true);
            const [success] = await handleEditOrder(
                editConfig.tableName,
                editConfig.rowId,
                editConfig.columnName,
                oldValue,
                newValue,
                editConfig.parentId,
            );
            setIsLoading(false);

            if (success) {
                editConfig.onSuccess?.();
            } else {
                // Revert on failure
                if (checked === undefined) {
                    setInternalChecked(!newChecked);
                }
                editConfig.onError?.();
            }
        }
    };
    
    // Custom font size mapping
    const getFontSize = (size: number): number => {
        if (size === 18) return 14;
        if (size === 16) return 12;
        if (size === 14) return 11;
        return size - 3; // Fallback for other sizes
    };
    
    const fontSize = getFontSize(size);

    return (
        <label className={`componentCheckbox-mainContainer ${isDisabled || isLoading ? 'componentCheckbox-mainContainer-disabled' : ''}`}>
            <input
                className='componentCheckbox-checkbox'
                type="checkbox"
                checked={isChecked}
                onChange={handleChange}
                disabled={isDisabled || isLoading}
            />
            <span 
                className={`componentCheckbox-customCheckbox ${style === 'minus' ? 'componentCheckbox-customCheckbox-minus' : ''} ${isDisabled || isLoading ? 'componentCheckbox-customCheckbox-disabled' : ''}`}
                style={{ 
                    width: `${size}px`,
                    height: `${size}px`,
                    fontSize: `${fontSize}px`,
                    borderColor: ((ghostUnchecked && !isChecked) || isDisabled)
                        ? 'var(--color-grey-medium)'
                        : undefined,
                }}
            >
            </span>
            <span className="componentCheckbox-label" style={((ghostUnchecked && !isChecked) || isDisabled) ? { color: 'var(--color-grey-medium)', marginLeft: `${labelGap}px` } : { color: 'var(--color-grey)', marginLeft: `${labelGap}px` }}>{label}</span>
        </label>
    );
};

export default Checkbox;